import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '../lib/api';
import { dateTimeSeconds, MYT, preciseTitle } from '../lib/time';
import { useAuth } from '../lib/auth';
import { Card, Table, Loading, Empty } from '../components/ui';

type User = {
  id: number;
  username: string;
  displayName: string;
  email: string | null;
  status: 'active' | 'disabled';
  mustChangePassword: boolean;
  lastLoginAt: string | null;
  roles: string[];
  locations: string[];
};

type Role = {
  code: string;
  name: string;
  description: string | null;
  isSystem: boolean;
  userCount: number;
  permissions: string[];
};

type Perm = { code: string; module: string; description: string };

export function AdminUsers() {
  const qc = useQueryClient();
  const { user: me } = useAuth();
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const users = useQuery({
    queryKey: ['admin-users'],
    queryFn: () => api.get<{ users: User[] }>('/api/v1/admin/users'),
  });
  const roles = useQuery({
    queryKey: ['admin-roles'],
    queryFn: () => api.get<{ roles: Role[] }>('/api/v1/admin/roles'),
  });
  const locations = useQuery({
    queryKey: ['admin-locations'],
    queryFn: () =>
      api.get<{ locations: Array<{ code: string; lines: number }> }>('/api/v1/admin/locations'),
  });

  const save = useMutation({
    mutationFn: (v: { id: number; body: Record<string, unknown> }) =>
      api.patch(`/api/v1/admin/users/${v.id}`, v.body),
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: ['admin-users'] });
    },
    onError: (e: Error) => setError(e.message),
  });

  const create = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post('/api/v1/admin/users', body),
    onSuccess: () => {
      setError(null);
      setCreating(false);
      void qc.invalidateQueries({ queryKey: ['admin-users'] });
    },
    onError: (e: Error) => setError(e.message),
  });

  return (
    <div className="space-y-6">
      {error && (
        <p role="alert" className="rounded bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </p>
      )}

      <Card
        title="Users"
        subtitle="Roles decide what a user can do; locations restrict which outlets' data they see. No locations means all of them."
        actions={
          <button
            onClick={() => setCreating((v) => !v)}
            className="rounded bg-brand-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-700"
          >
            {creating ? 'Cancel' : 'New user'}
          </button>
        }
      >
        {creating && (
          <NewUserForm
            roles={roles.data?.roles ?? []}
            locations={(locations.data?.locations ?? []).map((l) => l.code)}
            onSubmit={(body) => create.mutate(body)}
            busy={create.isPending}
          />
        )}

        {users.isLoading ? (
          <Loading />
        ) : (
          <Table
            head={['Username', 'Name', 'Roles', 'Locations', 'Status', `Last login (${MYT})`, '']}
            rows={(users.data?.users ?? []).map((u) => [
              <span className="font-medium text-slate-900">{u.username}</span>,
              u.displayName,
              u.roles.join(', ') || '—',
              u.locations.length ? u.locations.join(', ') : 'all',
              <span
                className={
                  u.status === 'active'
                    ? 'rounded bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700'
                    : 'rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-600'
                }
              >
                {u.status}
              </span>,
              // 'never' rather than a dash: an account that has never been used is a different
              // thing from one whose last login we failed to record.
              <span title={preciseTitle(u.lastLoginAt)}>
                {dateTimeSeconds(u.lastLoginAt, 'never')}
              </span>,
              // You cannot disable yourself; the server refuses too, but not offering it is kinder.
              u.username === me?.username ? (
                <span className="text-xs text-slate-400">you</span>
              ) : (
                <button
                  onClick={() =>
                    save.mutate({
                      id: u.id,
                      body: { status: u.status === 'active' ? 'disabled' : 'active' },
                    })
                  }
                  className="rounded border border-slate-300 px-2 py-1 text-xs hover:bg-brand-50"
                >
                  {u.status === 'active' ? 'Disable' : 'Enable'}
                </button>
              ),
            ])}
          />
        )}
      </Card>
    </div>
  );
}

function NewUserForm({
  roles,
  locations,
  onSubmit,
  busy,
}: {
  roles: Role[];
  locations: string[];
  onSubmit: (body: Record<string, unknown>) => void;
  busy: boolean;
}) {
  const [f, setF] = useState({
    username: '',
    displayName: '',
    email: '',
    password: '',
    roles: [] as string[],
    locations: '',
    mustChangePassword: false,
  });

  return (
    <form
      className="mb-6 grid gap-3 rounded border border-slate-200 bg-slate-50 p-4 sm:grid-cols-2"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit({
          ...f,
          locations: f.locations
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean),
        });
      }}
    >
      {(
        [
          ['username', 'Username', 'text'],
          ['displayName', 'Display name', 'text'],
          ['email', 'Email (optional)', 'email'],
          ['password', 'Initial password', 'password'],
        ] as const
      ).map(([key, label, type]) => (
        <label key={key} className="text-xs text-slate-600">
          {label}
          <input
            type={type}
            required={key !== 'email'}
            value={f[key]}
            onChange={(e) => setF({ ...f, [key]: e.target.value })}
            className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
          />
        </label>
      ))}

      <label className="text-xs text-slate-600 sm:col-span-2">
        Locations — leave blank for all. Codes come from the ledger, so they cannot be mistyped: a
        code that does not exist scopes the user to nothing.
        <input
          list="location-codes"
          value={f.locations}
          onChange={(e) => setF({ ...f, locations: e.target.value })}
          placeholder="e.g. PG, JB"
          className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
        />
        <datalist id="location-codes">
          {locations.map((c) => (
            <option key={c} value={c} />
          ))}
        </datalist>
      </label>

      <fieldset className="sm:col-span-2">
        <legend className="text-xs text-slate-600">Roles</legend>
        <div className="mt-1 flex flex-wrap gap-3">
          {roles.map((r) => (
            <label key={r.code} className="flex items-center gap-1.5 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={f.roles.includes(r.code)}
                onChange={(e) =>
                  setF({
                    ...f,
                    roles: e.target.checked
                      ? [...f.roles, r.code]
                      : f.roles.filter((x) => x !== r.code),
                  })
                }
              />
              {r.name}
            </label>
          ))}
        </div>
      </fieldset>

      <label className="flex items-center gap-2 text-sm text-slate-700 sm:col-span-2">
        <input
          type="checkbox"
          checked={f.mustChangePassword}
          onChange={(e) => setF({ ...f, mustChangePassword: e.target.checked })}
          className="rounded border-slate-300"
        />
        Require a password change on first sign-in
      </label>

      <div className="sm:col-span-2">
        <button
          type="submit"
          disabled={busy}
          className="rounded bg-brand-600 px-4 py-1.5 text-sm text-white hover:bg-brand-700 disabled:opacity-50"
        >
          {busy ? 'Creating…' : 'Create user'}
        </button>
        <span className="ml-3 text-xs text-slate-500">Any password is accepted.</span>
      </div>
    </form>
  );
}

export function AdminRoles() {
  const roles = useQuery({
    queryKey: ['admin-roles'],
    queryFn: () => api.get<{ roles: Role[] }>('/api/v1/admin/roles'),
  });
  const perms = useQuery({
    queryKey: ['admin-perms'],
    queryFn: () => api.get<{ permissions: Perm[] }>('/api/v1/admin/permissions'),
  });

  const byModule = (perms.data?.permissions ?? []).reduce<Record<string, Perm[]>>((acc, p) => {
    (acc[p.module] ??= []).push(p);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      <Card title="Roles" subtitle="A user's permissions are the union of their roles.">
        {roles.isLoading ? (
          <Loading />
        ) : (
          <Table
            head={['Role', 'Description', 'Users', 'Permissions']}
            rows={(roles.data?.roles ?? []).map((r) => [
              <span className="font-medium text-slate-900">{r.name}</span>,
              r.description ?? '—',
              String(r.userCount),
              `${r.permissions.length} of ${perms.data?.permissions.length ?? '—'}`,
            ])}
          />
        )}
      </Card>

      <Card
        title="Permissions"
        subtitle="The vocabulary. Defined in code — this list is generated from it, so it can never drift."
      >
        {perms.isLoading ? (
          <Loading />
        ) : (
          <div className="space-y-5">
            {Object.entries(byModule).map(([mod, list]) => (
              <div key={mod}>
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  {mod}
                </h3>
                <Table
                  head={['Code', 'Description', ...(roles.data?.roles ?? []).map((r) => r.code)]}
                  rows={list.map((p) => [
                    <code className="text-xs text-slate-800">{p.code}</code>,
                    p.description,
                    ...(roles.data?.roles ?? []).map((r) =>
                      r.permissions.includes(p.code) ? (
                        <span className="text-emerald-600">✓</span>
                      ) : (
                        <span className="text-slate-300">·</span>
                      ),
                    ),
                  ])}
                />
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

export function AdminAudit() {
  const audit = useQuery({
    queryKey: ['admin-audit'],
    queryFn: () =>
      api.get<{
        entries: Array<{
          id: number;
          actor: string;
          action: string;
          entity_type: string | null;
          entity_id: string | null;
          ip: string | null;
          created_at: string;
        }>;
      }>('/api/v1/admin/audit?limit=200'),
    refetchInterval: 30_000,
  });

  return (
    <Card
      title="Audit log"
      subtitle="Append-only. Logins, permission changes and user administration."
    >
      {audit.isLoading ? (
        <Loading />
      ) : (audit.data?.entries ?? []).length === 0 ? (
        <Empty>No entries yet.</Empty>
      ) : (
        <Table
          head={[`When (${MYT})`, 'Actor', 'Action', 'Entity', 'IP']}
          rows={(audit.data?.entries ?? []).map((e) => [
            // An audit trail is read to the second — "who changed this, and was it before or
            // after the incident" is not a question minute precision answers.
            <span title={preciseTitle(e.created_at)}>{dateTimeSeconds(e.created_at)}</span>,
            e.actor,
            <code className="text-xs">{e.action}</code>,
            [e.entity_type, e.entity_id].filter(Boolean).join(' ') || '—',
            e.ip ?? '—',
          ])}
        />
      )}
    </Card>
  );
}
